import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getAllocatedBudgets } from '../../../api/buget-allocation/get-budget-allocations'
import { getCenters } from '../../../api/centers/get-centers'
import { CurrencyEnum, currencyEnumMapping } from '../../../lib/types/enums/common/currency.enum'
import { BudgetAllocationInput } from '../../../lib/types/models/budget-line/budget-allocation-input'
import { BaseFormProps, NullableTransformer } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'

const formSchema: ZodType<NullableTransformer<BudgetAllocationInput>> = z.object({
  centerId: createNumberSchema('Centre'),
  price: createNumberSchema('Montant', { isPositive: true }),
  currency: createEnumSchema({ fieldName: 'Devise', enumType: CurrencyEnum })
})

const BudgetAllocationForm = ({
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<BudgetAllocationInput>) => {
  const form = useForm<BudgetAllocationInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: centers } = useQuery({
    queryKey: ['centers'],
    queryFn: () => getCenters({ version: 'BASIC' })
  })
  const { data } = useQuery({
    queryKey: ['budget-allocations'],
    queryFn: () => getAllocatedBudgets()
  })

  const filteredCenters =
    centers?.data.filter((center) => {
      const allocatedBudget = data?.data.find((budget) => budget.centerId === center.id)
      return !allocatedBudget
    }) ?? []

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <InnerBox className={'p-4 rounded-2xl flex flex-col gap-4'}>
          <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
            Budget
          </Typography>

          <div className="flex gap-2 justify-between">
            <FormField
              control={form.control}
              name="centerId"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledSelectInput
                      required
                      {...field}
                      searchable
                      label="Centre"
                      options={
                        filteredCenters.map((center) => ({
                          label: center.name,
                          value: center.id
                        })) || []
                      }
                      onChange={(option) => field.onChange(option)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledInput required type="number" label="Montant" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      required
                      label={'Devise'}
                      options={Object.values(CurrencyEnum).map((currency) => ({
                        label: currencyEnumMapping[currency].label,
                        value: currency
                      }))}
                      selectedOption={getSelectedEnumOption(field.value, currencyEnumMapping)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </InnerBox>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default BudgetAllocationForm
